<template>
    <div class="col-12">
        <TransitionGroup name="fade">
            <ItemRow
                v-for="(item, index) in items"
                :key="index"
                :canDeleteItem="canDeleteItem"
                :disabled="disabled"
                :errors="errors"
                :item="item"
                :itemIndex="index"
                :requiredFields="requiredFields"
                :permissionsAccess="permissionsAccess"
                :unitPriceCalculation="unitPriceCalculation"
                :vatRates="vatRates"
                :isSymbolVisible="isSymbolVisible"
                :standardItems="standardItems"
                @inputs="$emit('itemInput', {index, ...$event})"
                @itemChanged="handleItemChanged"
                @removeItem="handleItemRemoved(index)"
                @addStandardItem="addStandardItem"
                @selectedVatRate="handleSelectedVatRate(index, $event)"
            />
        </TransitionGroup>

        <hr>

        <RoundingRow :roundings="roundings"/>

        <SummaryRow
            ref="summaryRow"
            :disabled="disabled"
            :errors="errors"
            :rounding="items.length > 1"
            :roundingInputs="['net_amount', 'gross_amount']"
            :roundings="roundings"
            :sums="summaries"
            @addNewItem="$emit('addNewItem')"
            @inputs="rounded"
        />
    </div>
</template>

<script>
import RoundingRow from "../default/RoundingRow";
import SummaryRow from "../default/SummaryRow";
import ItemRow from "./ItemRow";
import hasErrors from "../../../../mixins/hasErrors";
import inputEmit from "../../../../mixins/inputEmit";
import hasVatRates from "../../../../mixins/hasVatRates";
import {forEach, groupBy, round} from "lodash";
import hasRequiredFields from "../../../../mixins/hasRequiredFields";
import hasPermissions from "../../../../mixins/hasPermissions";

export default {
    name: "AddingItemsSection",
    components: {ItemRow, SummaryRow, RoundingRow},
    mixins: [hasErrors, inputEmit, hasVatRates, hasRequiredFields, hasPermissions],
    props: {
        items: {
            type: Array,
            required: true,
        },
        unitPriceCalculation: {
            type: Boolean,
            required: true,
        },

        disabled: {
            type: Boolean,
            default: false,
        },
        isSymbolVisible: {
            type: Number,
            default: 0,
        },
        standardItems: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            roundings: {
                gross: 0,
                net: 0,
                vat: 0,
            },
            summaries: {
                gross: 0,
                net: 0,
                vat: 0,
            },
        };
    },
    computed: {
        groupedByVatRate() {
            return groupBy(this.items, item => item.vat_rate);
        },
        canDeleteItem() {
            return this.items.length > 1;
        },
        sumItemsGrossAmount() {
            return this.parseMoney(this.getSum('gross_amount'));
        },
        sumItemsNetAmount() {
            return this.parseMoney(this.getSum('net_amount'));
        },
        sumItemsVatAmount() {
            return this.parseMoney(this.summaries.gross) - this.parseMoney(this.summaries.net);
        },
        roundingsNet() {
            return this.rounding('net');
        },
        roundingsVat() {
            return this.rounding('vat');
        },
        roundingsGross() {
            return this.rounding('gross');
        },
    },
    methods: {
        addStandardItem(index){
            this.$emit('addStandardItem', index)
        },
        getSum(field) {
            return this.items.reduce((prevVal, item) => prevVal + item[field], 0);
        },
        parseMoney(number) {
            number = this.$options.filters.money(number);
            return this.$options.filters.floatMoney(number);
        },
        calculateSummary() {
            this.summaries.gross = this.sumItemsGrossAmount;
            this.summaries.net = this.sumItemsNetAmount;
            this.summaries.vat = this.sumItemsVatAmount;
        },
        emitSummaries(withCalculate = true) {
            if (withCalculate) {
                this.calculateSummary();
            }
            this.calculateRoundings();
            this.emitInput('net_amount', this.summaries.net);
            this.emitInput('gross_amount', this.summaries.gross);
            this.emitInput('vat_amount', this.summaries.vat);
        },
        calculateRoundings() {
            this.roundings.gross = this.roundingsGross;
            this.roundings.net = this.roundingsNet;
            this.roundings.vat = this.roundingsVat;
        },
        rounding(field) {
            const itemsRowSum = this.getSum(`${field}_amount`);
            const itemComputedSum = this.$options.filters.floatMoney(this.summaries[field]);
            let result = itemComputedSum - itemsRowSum;
            result = Math.round(result * 100) / 100;

            return result;
        },
        handleSelectedVatRate(index, vatRateId) {
            const vatRate = this.getVatRate(vatRateId);
            if (vatRate.status === 'exempt') {
                this.$emit('hasExemptVat');
            }
            this.$emit('itemInput', {index, name: 'vat_rate', value: vatRateId});
            this.$refs.summaryRow.resetThreshold();

        },
        rounded({name, value}) {
            this.summaries[name] = round(value, 2);
            this.sumVatAmountAndEmitSummaries();
        },
        sumVatAmountAndEmitSummaries() {
            this.summaries.vat = this.sumItemsVatAmount;
            this.emitSummaries(false);
        },
        handleItemChanged() {
            this.emitSummaries();
            this.$refs.summaryRow.resetThreshold();
        },
        handleItemRemoved(index) {
            this.$emit('removeItem', index);
            this.$refs.summaryRow.resetThreshold();
        },
        initSummaries(summaries) {
            forEach(summaries, (value, name) => this.summaries[name] = value);
        },
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>

